/**
 * Auth helper
 */

import { _isNil, _isNotBlank, _isNotEmpty, _noop, _notNil } from '@/littledash';
import type { Study } from '@/model/Study.model';
import type { StudyUserSummaryV1, StudyUserTransformer } from '@/model/User.model';
import { RouteService } from '@/support/RouteService';
import store from '@/store';

export const getCurrentUser = () => store.getState().user.currentUser;

/**
 * The pages that establish a session: `/login`, `/forgot-password` and `/reset-password`.
 * Matched by pattern rather than read from `notLoggedInRoutes` in `@/routes/routes` because that
 * table eagerly imports the whole component tree, and `auth.ts` is a dependency of
 * `@/support/http` — importing it would invert the dependency graph and cycle back
 * through `@/helpers`. This deliberately excludes the other unauthenticated routes
 * (`/maintenance`, `/rate-limited`, `/debug`, `/unsupported-client-version`), which own
 * no auth state and so are safe to reload.
 */
const authRoutePattern = /^\/(?:login|forgot-password|reset-password)\/?$/;

/**
 * Whether a pathname is one of the authentication pages. These pages drive the login
 * handshake themselves, so a leftover expired token is boot garbage rather than a session
 * that just ended — and a session-expired logout would reload the page out from under an
 * in-flight flow (e.g. SSO short-lived-token redemption on `/login?identifier=…`).
 */
export const isAuthRoute = (pathname: string): boolean => authRoutePattern.test(pathname);

/**
 * A toast to re-show on the login page once the post-logout reload completes.
 * Carried across the reload via history.state, since the in-memory app that
 * would normally raise the toast is destroyed by the reload.
 */
export interface LogoutMessage {
  level: 'info' | 'warning';
  title: string;
  body?: string;
}

/**
 * The reason a logout was triggered. The reason alone determines the post-logout
 * behaviour (toast to re-show, whether to return to the previous page, whether to
 * purge browser caches), so callers only express intent — never the mechanics.
 */
export type LogoutOptions =
  | { type: 'user' }
  | { type: 'inactivity' }
  | { type: 'session-expired'; returnTo?: string }
  | { type: 'stale-token' }
  | { type: 'outdated-client'; redirectTo: string };

type LogoutState = {
  message?: LogoutMessage;
  returnTo?: string;
};

const POST_LOGOUT_STATE_KEY = 'logout_state';

/**
 * Purge CacheStorage, cookies and IndexedDB in addition to web storage. Used by
 * the outdated-client recovery flow to guarantee no stale assets survive.
 */
const clearBrowserCaches = async (): Promise<void> => {
  // Best-effort: never let a rejection (or a synchronous throw such as Firefox not
  // implementing indexedDB.databases) escape and abort the logout redirect.
  try {
    if ('caches' in window) {
      await window.caches.keys().then((keys) => Promise.all(keys.map((key) => window.caches.delete(key).catch(_noop))));
    }
    document.cookie.split(';').forEach((cookie) => {
      const eqPos = cookie.indexOf('=');
      const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
    if ('cookieStore' in window) {
      await window.cookieStore
        .getAll()
        // @ts-expect-error: not supported in all browsers
        .then((cookies) => Promise.all(cookies.map((cookie) => window.cookieStore.delete(cookie.name))));
    }
    if ('indexedDB' in window) {
      await indexedDB.databases().then((dbs) => {
        dbs.forEach((db) => {
          if (_notNil(db.name)) {
            indexedDB.deleteDatabase(db.name);
          }
        });
      });
    }
  } catch {
    // swallow — clearing caches is best-effort and must not block logout
  }
};

/**
 * The current same-origin location as a relative path, for post-login restore.
 * Shared by the logout callers that carry a returnTo so they stay consistent.
 */
export const currentReturnTo = (): string => `${window.location.pathname}${window.location.search}`;

const loginRoute = (): string => RouteService.web({ route: 'login' }).route;

/**
 * Wipe all persisted state and reload the login page, re-raising `state` as a
 * post-logout toast (and optional return path) that survives the reload via
 * history.state — read back by the login page through {@link consumePostLogout}.
 */
const reloadToLoginWithToast = (state: LogoutState): void => {
  localStorage.clear();
  sessionStorage.clear();
  window.history.replaceState({ [POST_LOGOUT_STATE_KEY]: state }, '', loginRoute());
  window.location.reload();
};

/**
 * Logs the current user out. The reason (see {@link LogoutOptions}) fully determines
 * the mechanics; each is handled explicitly below:
 *
 * - `inactivity` / `session-expired`: wipe storage and reload to the login page,
 *   carrying a toast (and, for session-expiry, a return path) across the reload.
 * - `user`: wipe storage and browser caches, then hard-navigate to a fresh login.
 * - `outdated-client`: as `user`, but navigate to a caller-supplied recovery target.
 * - `stale-token`: quiet — drop only the token from storage and the redux store, with
 *   no wipe, navigation or reload, so it can discard boot garbage on an authentication
 *   page without disturbing that page's own in-flight login flow.
 *
 * The full page reload / navigate discards the in-memory redux store, so no stale
 * session state can survive into the next login.
 */
export const logout = async (options: LogoutOptions = { type: 'user' }): Promise<void> => {
  store.dispatch({ type: 'LOGOUT' });

  switch (options.type) {
    case 'stale-token':
      localStorage.removeItem('token');
      return;

    case 'inactivity':
      reloadToLoginWithToast({ message: { level: 'warning', title: 'Logged out due to inactivity' } });
      return;

    case 'session-expired':
      reloadToLoginWithToast({
        message: { level: 'info', title: 'Session expired', body: 'Please log in again' },
        returnTo: options.returnTo,
      });
      return;

    case 'outdated-client':
      localStorage.clear();
      sessionStorage.clear();
      await clearBrowserCaches();
      window.location.replace(options.redirectTo);
      return;

    case 'user':
      localStorage.clear();
      sessionStorage.clear();
      await clearBrowserCaches();
      window.location.replace(loginRoute());
      return;
  }
};

/**
 * Read and clear the post-logout payload stashed on history.state before the reload.
 * The return path is validated as a same-origin relative URL to guard against
 * open redirects, and rejected if it points back at an authentication page (e.g. a
 * `/login?identifier=<dead SLT>` captured mid-redemption), which would otherwise
 * bounce a freshly logged-in user straight back into a spent login flow.
 */
export const consumePostLogout = (): LogoutState | undefined => {
  const state = window.history.state;
  const postLogout: LogoutState | undefined = state?.[POST_LOGOUT_STATE_KEY];
  if (_isNil(postLogout)) {
    return undefined;
  }
  const rest = { ...state };
  delete rest[POST_LOGOUT_STATE_KEY];
  window.history.replaceState(rest, '');
  const isSafeRelativePath =
    _isNotBlank(postLogout.returnTo) && postLogout.returnTo.startsWith('/') && !postLogout.returnTo.startsWith('//');
  const returnTo =
    isSafeRelativePath && !isAuthRoute(new URL(postLogout.returnTo as string, window.location.origin).pathname)
      ? postLogout.returnTo
      : undefined;
  return { message: postLogout.message, returnTo };
};

type StudyUserCondition = (user: StudyUserTransformer) => boolean;

export function isStudyUser(study: Study | undefined, condition?: StudyUserCondition): boolean {
  const user = getCurrentUser();
  const users = (Array.isArray(study?.users) ? study?.users : study?.users?.data) ?? [];
  return users.some((u) => u.id === user.id && (condition?.(u) ?? true));
}

export function isAuthor(study: Study) {
  const user = getCurrentUser();
  return Number(user.id) === Number(study?.author?.id);
}

export const isOwner = (study: Study) => {
  const user = getCurrentUser();
  return Number(user.id) === Number(study?.owner?.id);
};

/**
 * Whether `study` is in an editable state: present, and not blocked by the
 * archived/cancelled guards. Shared by the write-user checks below.
 */
const isStudyEditable = (
  study: Study | undefined,
  disableArchived: boolean,
  disableCancelled: boolean
): study is Study => {
  if (_isNil(study)) {
    return false;
  }
  return !((disableArchived && study.archived_at) || (disableCancelled && study.canceled_at));
};

export function isWriteUserForStudy(study: Study, disableArchived = true, disableCancelled = true) {
  if (!isStudyEditable(study, disableArchived, disableCancelled)) {
    return false;
  }
  return isStudyUser(study, (u: StudyUserTransformer) => u.access === 'write') || isOwner(study) || isAuthor(study);
}

export const isWriteUserForStudyUsers = (studyUsers: Array<StudyUserSummaryV1>) => {
  if (_isNotEmpty(studyUsers)) {
    const user = getCurrentUser();
    return studyUsers.some((u) => u.id === user.id && u.access === 'write');
  }
  return false;
};

export const isWriteUserForStudyWithUsers = (
  study: Study | undefined,
  studyUsers: Array<StudyUserSummaryV1>,
  disableArchived = true,
  disableCancelled = true
): boolean => {
  if (!isStudyEditable(study, disableArchived, disableCancelled)) {
    return false;
  }
  return isWriteUserForStudyUsers(studyUsers) || isOwner(study) || isAuthor(study);
};

export function isCurrentTeamOwnerWithSettingsAccess() {
  try {
    const team = store.getState().team;
    return team.team.pivot.owner && !(team.features?.disable_admin_study_settings_access ?? false);
  } catch (e) {
    return false;
  }
}

export function isCurrentTeamOwner() {
  try {
    const team = store.getState().team.team;
    return team.pivot.owner;
  } catch (e) {
    return false;
  }
}

export const isCurrentTeamAdminConsoleUser = () => {
  try {
    return isCurrentTeamOwner() && store.getState().team.team.pivot.admin_console_user;
  } catch (e) {
    return false;
  }
};

export function hasStudiesReadAll(): boolean {
  try {
    return store.getState().team.team.studies_read_all ?? false;
  } catch (e) {
    return false;
  }
}
